import { Outlet, createFileRoute, useChildMatches, useNavigate } from '@tanstack/react-router';
import { Loader2, MessageSquare, Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { type TicketSummary, createTicket, listMyTickets } from '~/api/support';
import { ApiError } from '~/lib/api-client';
import { useSession } from '~/lib/auth';
import { buildHead } from '~/seo/site';

export const Route = createFileRoute('/support')({
  head: () =>
    buildHead({
      title: 'Support',
      description: 'Get help with your Prod Battle account.',
      path: '/support',
      noindex: true,
    }),
  component: SupportRoute,
});

// /support is the parent layout for /support/$id (TanStack file routing
// treats sibling files this way). When a child route is active we must
// render its <Outlet/> instead of the list, otherwise the parent
// component "swallows" the navigation and you see the list at the
// child URL - which is exactly how the missing-description bug
// manifested in the wild.
function SupportRoute() {
  const childMatches = useChildMatches();
  if (childMatches.length > 0) return <Outlet />;
  return <SupportPage />;
}

function SupportPage() {
  const { data: session, isLoading } = useSession();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!session) {
    return (
      <section className="mx-auto max-w-lg px-4 py-20 text-center">
        <MessageSquare className="mx-auto mb-4 size-10 text-muted-foreground" />
        <h1 className="mb-2 text-2xl font-bold">Support</h1>
        <p className="mb-6 text-muted-foreground">Sign in to access support.</p>
        <button
          type="button"
          onClick={() => navigate({ to: '/auth/sign-in' })}
          className="rounded-md bg-primary px-4 py-2 font-mono text-sm font-bold uppercase tracking-wider text-primary-foreground"
        >
          Sign in
        </button>
      </section>
    );
  }

  return <SupportList />;
}

function SupportList() {
  const [tickets, setTickets] = useState<TicketSummary[]>([]);
  const [quotaRemaining, setQuotaRemaining] = useState(4);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();

  async function load() {
    setLoading(true);
    try {
      const res = await listMyTickets();
      setTickets(res.items);
      setQuotaRemaining(res.quotaRemaining);
    } finally {
      setLoading(false);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: load runs once on mount; no deps needed
  useEffect(() => {
    load();
  }, []);

  const statusBadge = (status: TicketSummary['status']) => {
    const cls = {
      open: 'bg-emerald-400/15 text-emerald-400',
      answered: 'bg-primary/15 text-primary',
      closed: 'bg-muted/40 text-muted-foreground',
    }[status];
    return (
      <span
        className={`rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${cls}`}
      >
        {status}
      </span>
    );
  };

  return (
    <section className="mx-auto max-w-2xl space-y-8 px-4 py-12">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">Support</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {quotaRemaining > 0
              ? `${quotaRemaining} new ticket${quotaRemaining === 1 ? '' : 's'} remaining today`
              : 'Daily ticket limit reached. Try again tomorrow.'}
          </p>
        </div>
        {quotaRemaining > 0 && !showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 font-mono text-sm font-bold uppercase tracking-wider text-primary-foreground"
          >
            <Plus className="size-3.5" aria-hidden />
            New ticket
          </button>
        )}
      </header>

      {showForm && (
        <NewTicketForm
          onClose={() => setShowForm(false)}
          onCreated={(id) => {
            navigate({ to: '/support/$id', params: { id } });
          }}
        />
      )}

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : tickets.length === 0 ? (
        <div className="rounded-lg border border-border bg-muted/20 py-12 text-center">
          <MessageSquare className="mx-auto mb-3 size-8 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">No tickets yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {tickets.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => navigate({ to: '/support/$id', params: { id: t.id } })}
              className="flex w-full items-center justify-between rounded-lg border border-border bg-card px-4 py-3 text-left transition-colors hover:border-foreground/30"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{t.subject}</p>
                <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                  {new Date(t.updatedAt).toLocaleDateString()} - {t.replyCount} reply
                  {t.replyCount !== 1 ? 'ies' : ''}
                </p>
              </div>
              <div className="ml-3 shrink-0">{statusBadge(t.status)}</div>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

function NewTicketForm({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const res = await createTicket({ subject, body });
      onCreated(res.id);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError('Daily ticket limit reached. Try again tomorrow.');
      } else {
        setError((err as Error).message ?? 'Something went wrong.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <h2 className="font-mono text-sm font-bold uppercase tracking-wider">New ticket</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Discard"
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </div>

      <div className="space-y-1">
        <label
          htmlFor="subject"
          className="font-mono text-xs uppercase tracking-wider text-muted-foreground"
        >
          Subject
        </label>
        <input
          id="subject"
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          required
          minLength={3}
          maxLength={120}
          placeholder="Brief description of the issue"
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
      </div>

      <div className="space-y-1">
        <label
          htmlFor="body"
          className="font-mono text-xs uppercase tracking-wider text-muted-foreground"
        >
          Details
        </label>
        <textarea
          id="body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          required
          minLength={10}
          maxLength={4000}
          rows={5}
          placeholder="Describe your issue in detail..."
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
        <p className="text-right font-mono text-[10px] text-muted-foreground">{body.length}/4000</p>
      </div>

      {error && (
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
      )}

      <button
        type="submit"
        disabled={saving}
        className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 font-mono text-sm font-bold uppercase tracking-wider text-primary-foreground disabled:opacity-60"
      >
        {saving && <Loader2 className="size-3.5 animate-spin" aria-hidden />}
        Submit ticket
      </button>
    </form>
  );
}
